import React, { useState, useEffect } from "react";
import DotRectangle from "../../../assets/dots-rectangle.svg";
import DotRectangle2 from "../../../assets/dots-rectangle2.svg";
import icon4 from "../../../assets/proSafeIcon4.png";
import { motion } from "framer-motion";
import ExcitingNewsModal from "./ExcitingNewsModal";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const HomeBanner = ({ data }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isDemoModalOpen, setDemoModalOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showNotificationBanner, setShowNotificationBanner] = useState(true);

  // Enhanced slider data with text overlay
  const sliderData = [
    {
      image:
        "https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      title: "Professional Safety Training",
      subtitle: "Expert-led courses for workplace safety",
      description:
        "Comprehensive training programs designed to keep you and your team safe",
    },
    {
      image:
        "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      title: "Industrial Safety Solutions",
      subtitle: "Warehouse and equipment safety",
      description:
        "Specialized training for industrial environments and equipment operation",
    },
    {
      image:
        "https://images.unsplash.com/photo-1584515933487-779824d29309?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      title: "Medical Emergency Training",
      subtitle: "First aid and CPR certification",
      description:
        "Life-saving skills for medical emergencies and first response situations",
    },
    {
      image:
        "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      title: "Hands-On Learning Experience",
      subtitle: "Practical training with real scenarios",
      description:
        "Learn through hands-on practice and real-world simulation exercises",
    },
  ];

  const toggleModal = () => {
    setModalOpen(!isModalOpen);
  };

  const toggleDemoModal = () => {
    setDemoModalOpen(!isDemoModalOpen);
  };

  const scrollToCourses = () => {
    // Try multiple possible IDs for the courses section
    const coursesSection =
      document.getElementById("courses-section") ||
      document.getElementById("courses") ||
      document.querySelector('[data-section="courses"]') ||
      document.querySelector(".courses-section");

    if (coursesSection) {
      coursesSection.scrollIntoView({ behavior: "smooth" });
    } else {
      // If no specific courses section found, scroll down by viewport height
      window.scrollBy({
        top: window.innerHeight,
        behavior: "smooth",
      });
    }
  };

  const dismissNotificationBanner = () => {
    setShowNotificationBanner(false);
  };

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderData.length);
    }, 5000); // Increased to 5 seconds for better readability

    return () => clearInterval(interval);
  }, [sliderData.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % sliderData.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + sliderData.length) % sliderData.length,
    );
  };

  return (
    <>
      {/* Full-width Notification Banner */}
      {showNotificationBanner && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          className="w-full bg-gradient-to-r from-[#B02526] via-[#d63031] to-[#B02526] text-white py-2 sm:py-1 px-2 sm:px-4 relative z-50"
        >
          {/* Close button - always in top right */}
          <button
            onClick={dismissNotificationBanner}
            className="absolute top-1 right-2 sm:top-2 sm:right-4 text-white hover:text-gray-200 transition-colors duration-200 z-10"
          >
            <XMarkIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          <div className="container mx-auto flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2 sm:gap-0 pr-8 sm:pr-0">
            <div className="flex flex-col sm:flex-row items-center space-y-1 sm:space-y-0 sm:space-x-4 text-center sm:text-left max-w-full">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                <span className="font-bold text-sm sm:text-base">
                  🎉 EXCITING NEWS
                </span>
              </div>
              <span className="text-xs sm:text-sm leading-tight">
                New Professional Safety Courses Available - Early Bird
                Registration Now Open!
              </span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0 sm:block hidden">
              <button
                onClick={toggleModal}
                className="bg-white/20 hover:bg-white/30 text-white px-3 sm:px-4 py-1 rounded-full text-xs font-semibold transition-all duration-300 backdrop-blur-sm whitespace-nowrap"
              >
                Learn More
              </button>
            </div>

            {/* Mobile Learn More button */}
            <div className="sm:hidden mt-1">
              <button
                onClick={toggleModal}
                className="bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-full text-xs font-semibold transition-all duration-300 backdrop-blur-sm"
              >
                Learn More
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Full-width Hero Slider Section */}
      <div className="relative w-full min-h-[600px] h-[600px] overflow-hidden">
        {/* Decorative Elements */}
        <div>
          <img
            className="absolute left-4 top-8 w-8 h-8 sm:w-10 sm:h-10 z-30"
            src={DotRectangle}
            alt=""
          />
        </div>

        {/* Full-width Slider Container */}
        <div className="relative w-full h-full">
          {/* Slider Images with Text Overlay */}
          {sliderData.map((slide, index) => (
            <motion.div
              key={index}
              className="absolute inset-0 w-full h-full"
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{
                opacity: index === currentSlide ? 1 : 0,
                scale: index === currentSlide ? 1 : 1.1,
              }}
              transition={{
                duration: 0.8,
                ease: "easeInOut",
              }}
            >
              {/* Background Image */}
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />

              {/* Dark Overlay for Better Text Readability */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/60 to-black/50"></div>

              {/* Text Overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white px-4 sm:px-8 lg:px-16 max-w-4xl">
                  <motion.h1
                    className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-4 leading-tight"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{
                      opacity: index === currentSlide ? 1 : 0,
                      y: index === currentSlide ? 0 : 30,
                    }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    {slide.title}
                  </motion.h1>

                  {/* <motion.h2
                    className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-6 text-[#B02526]"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ 
                      opacity: index === currentSlide ? 1 : 0, 
                      y: index === currentSlide ? 0 : 20 
                    }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  >
                    {slide.subtitle}
                  </motion.h2> */}

                  <motion.p
                    className="text-lg sm:text-xl md:text-2xl font-medium mb-8 max-w-2xl mx-auto leading-relaxed"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{
                      opacity: index === currentSlide ? 1 : 0,
                      y: index === currentSlide ? 0 : 20,
                    }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                  >
                    {slide.description}
                  </motion.p>

                  <motion.div
                    className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{
                      opacity: index === currentSlide ? 1 : 0,
                      y: index === currentSlide ? 0 : 20,
                    }}
                    transition={{ duration: 0.8, delay: 0.8 }}
                  >
                    <motion.button
                      onClick={scrollToCourses}
                      className="bg-gradient-to-r from-[#B02526] to-[#d63031] hover:from-[#8B1F20] hover:to-[#b71c1c] text-white font-bold px-8 py-4 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-2xl text-lg"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {data.main_button}
                    </motion.button>

                    <motion.button
                      onClick={toggleDemoModal}
                      className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-300 border-2 border-white/30 text-lg"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {data.secondary_button}
                    </motion.button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Navigation Arrows */}
          <motion.button
            onClick={prevSlide}
            className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full p-4 shadow-xl transition-all duration-300 hover:scale-110 z-10 group"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronLeftIcon className="w-6 h-6 text-[#183864] group-hover:text-[#B02526] transition-colors duration-300" />
          </motion.button>

          <motion.button
            onClick={nextSlide}
            className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full p-4 shadow-xl transition-all duration-300 hover:scale-110 z-10 group"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronRightIcon className="w-6 h-6 text-[#183864] group-hover:text-[#B02526] transition-colors duration-300" />
          </motion.button>

          {/* Slide Indicators */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-10">
            {sliderData.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? "bg-white w-8 shadow-lg"
                    : "bg-white/60 hover:bg-white/80 w-3"
                }`}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </div>
        </div>

        {/* Decorative Elements */}
        <div>
          <img
            className="absolute right-4 bottom-8 z-30 w-8 h-8 sm:w-10 sm:h-10"
            src={DotRectangle2}
            alt=""
          />
        </div>
      </div>

      {/* Content Section Below Slider */}
      <div className="relative bg-white py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            {/* Essential Information */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {/* Main Heading */}
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-[#183864] mb-4">
                  {data.first_heading}, {data.middle_heading},{" "}
                  {data.last_heading}
                  <motion.img
                    className="inline w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 ml-4"
                    src={icon4}
                    alt=""
                    whileHover={{ scale: 1.1, rotate: 10 }}
                    transition={{ duration: 0.3 }}
                  />
                </h2>

                <p className="text-lg sm:text-xl text-[#5E6282] font-medium leading-relaxed">
                  {data.tag_line}
                </p>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap justify-center items-center gap-8 pt-8 border-t border-gray-200">
                <div className="flex items-center text-sm text-gray-600">
                  <span className="text-2xl mr-2">🏆</span>
                  <span className="font-semibold">20+ Years Experience</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <span className="text-2xl mr-2">📜</span>
                  <span className="font-semibold">Certified Training</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <span className="text-2xl mr-2">👥</span>
                  <span className="font-semibold">Expert Instructors</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <ExcitingNewsModal isOpen={isModalOpen} onRequestClose={toggleModal} />

      {/* Enhanced Demo Video Modal */}
      {isDemoModalOpen && (
        <motion.div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[99999] flex items-center justify-center p-4 overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="relative">
              <button
                onClick={toggleDemoModal}
                className="absolute top-6 right-6 z-10 bg-black/20 hover:bg-black/40 backdrop-blur-sm text-white rounded-full p-3 transition-all duration-200"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>

              <div className="p-8 overflow-hidden">
                <h2 className="text-3xl font-bold text-[#183864] mb-6 text-center">
                  ProSafe Training Experience
                </h2>

                {/* Enhanced Video Container */}
                <div className="aspect-video bg-gray-100 rounded-2xl overflow-hidden shadow-inner mb-6">
                  <video
                    controls
                    autoPlay
                    className="w-full h-full object-cover"
                    poster="https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                  >
                    <source
                      src="https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4"
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                </div>

                <div className="text-center">
                  <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                    Experience our world-class professional safety training
                    programs. See how we prepare students for real-world
                    scenarios with hands-on learning and expert instruction.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button
                      onClick={() => {
                        toggleDemoModal();
                        scrollToCourses();
                      }}
                      className="bg-gradient-to-r from-[#B02526] to-[#d63031] hover:from-[#8B1F20] hover:to-[#b71c1c] text-white font-semibold px-8 py-4 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg"
                    >
                      Explore Our Courses
                    </button>
                    <button
                      onClick={toggleDemoModal}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-8 py-4 rounded-2xl transition-all duration-300"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default HomeBanner;
